import { Box, Button, Divider, Stack, Typography, Grid2 as Grid, CircularProgress, Avatar } from "@mui/material";
import { updateComment, handleRetrieveAllDraft } from "../../../redux/guest-slices/common.slice";
import { handleRetrieveSingleForum } from "../../../redux/public-slices/forum.slice";
import { handleRetrieveSingleBlog } from "../../../redux/public-slices/blog.slice";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FILE_PATH_URI } from "../../../utils/helper";
import { useEffect, useRef, useState } from "react";
import { useToast } from "../../../hooks/useToast";
import SkeletonImage from "../../ui/SkeletonImage";
import InputFieldB from "../../ui/InputFieldB";

function EditComment() {
  const toast = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const formRef = useRef("");
  const [content, setContent] = useState({});
  const [comment, setComment] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);
  const [helperText, setHelperText] = useState([]);
  const { id } = useParams();
  const { loadings } = useSelector((state) => state.otherSlice);

  const handleUpdateComment = async (isDraft) => {
    const form = formRef.current;
    const formData = Object.fromEntries(new FormData(form));
    formData.isDraft = isDraft;

    if (comment.forumId) {
      formData.forumId = comment.forumId;
    } else {
      formData.blogId = comment.blogId;
    }

    if (!formData.description || formData.description.trim() === "") {
      setHelperText(["Description is required"]);
      toast.error("Please enter a description!");
      return;
    }

    try {
      await dispatch(updateComment({ id, formData })).unwrap();
      toast.success("Comment updated successfully!");
      setTimeout(() => {
        navigate(-1);
      }, 500);
    } catch (error) {
      toast.error("Failed to update comment");
    }
  };
  const retrieveData = async () => {
    try {
      const draftResponse = await dispatch(handleRetrieveAllDraft()).unwrap();

      const drafts = draftResponse?.records || [];
      const commentData = drafts.find((draft) => draft._id === id);
      setComment(commentData);

      if (commentData?.forumId) {
        const { forum } = await dispatch(
          handleRetrieveSingleForum(commentData.forumId)
        ).unwrap();
        setContent(forum || {});
      } else if (commentData?.blogId) {
        const { blog } = await dispatch(
          handleRetrieveSingleBlog(commentData.blogId)
        ).unwrap();
        setContent(blog || {});
      }

      setDataLoaded(true);
    } catch (error) {
      setDataLoaded(true);
    }
  };

  useEffect(() => {
    retrieveData();
    // eslint-disable-next-line
  }, [id]);

  if (!dataLoaded) {
    return (
      <Box
        sx={{
          p: 3,
          height: "100%",
          bgcolor: "white",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography>Loading comment details...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        height: "100%",
        bgcolor: "white",
        borderRadius: "8px",
      }}
    >
      <Box
        sx={{
          gap: 2,
          display: "flex",
          flexWrap: "wrap",
        }}
      >
        <Stack
          spacing={2}
          direction="row"
          sx={{
            alignItems: "center",
            flexWrap: { xs: "wrap" },
          }}
          divider={<Divider flexItem orientation="vertical" />}
        >
          <Avatar sx={{ width: 35, height: 35 }} src={content?.user?.image} />
          <Typography
            fontWeight="600"
            color="other.color1"
            textTransform="capitalize"
          >
            {content?.user?.fullName}
          </Typography>
        </Stack>

        <Divider
          flexItem
          orientation="vertical"
          sx={{
            display: { xs: "none", sm: "block" },
          }}
        />

        <Stack
          spacing={2}
          direction="row"
          sx={{
            alignItems: "center",
            flexWrap: { xs: "wrap" },
          }}
          divider={<Divider flexItem orientation="vertical" />}
        ></Stack>
      </Box>

      <Divider sx={{ mt: 2 }} />

      <Typography
        sx={{
          my: 2,
          width: "100%",
          overflow: "hidden",
          WebkitLineClamp: 1,
          color: "other.color1",
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
        }}
        fontSize={28}
        fontWeight="700"
      >
        {content?.title}
      </Typography>

      <Box
        sx={{
          mb: 2,
          width: "100%",
          maxHeight: "300px",
          overflow: "hidden",
          borderRadius: "6px",
          aspectRatio: "1/0.5",
        }}
      >
        <SkeletonImage src={FILE_PATH_URI + content?.banner?.path} />
      </Box>

      {/* Edit Form */}
      <Box className="page-content">
        <Grid container spacing={3} component="form" ref={formRef}>
          <Grid size={12}>
            <InputFieldB
              type="textarea"
              label="Comments *"
              name="description"
              variant="outlined"
              helperText={helperText}
              labelvariant="InputLabelA"
              placeholder="Write your comment here.."
              defaultValue={comment?.description || ""}
            />
          </Grid>

          <Grid size={12}>
            <Box
              sx={{
                gap: 1,
                maxWidth: 600,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Button
                type="button"
                variant="contained"
                sx={{
                  minWidth: "150px",
                  justifyContent: "center",
                }}
                endIcon={
                  loadings?.create && (
                    <CircularProgress size={22} color="text" thickness={6} />
                  )
                }
                onClick={() => {
                  handleUpdateComment(false);
                }}
              >
                Submit
              </Button>
              <Button
                type="button"
                variant="contained"
                sx={{
                  minWidth: "150px",
                  justifyContent: "center",
                }}
                endIcon={
                  loadings?.create && (
                    <CircularProgress size={22} color="text" thickness={6} />
                  )
                }
                onClick={() => {
                  handleUpdateComment(true);
                }}
              >
                Save Draft
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default EditComment;
